import { useState, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'

import useStripeServices, { subscriptionIntervals } from './useStripeServices'
import useModalTakeover from 'src/widgets/useModalTakeover'
import csrfHeaders from 'src/utils/csrfHeaders'

const BANK_CONNECTION_ERROR =
  'Failed to initialize bank connection. Please reload the page and try again.'

const ACH_DRAFT = { payment_source: 'stripe_card', payment_method_used: 'ACH' }

const NO_LINK = { email: null, accounts: [] }

const isEligible = account =>
  account.supported_payment_method_types?.includes('us_bank_account')

const useBank = ({
  i18n, stripe, donation, record, bankProps,
  failed, finished,
  enable_upsell, stripeKey,
}) => {
  const [ loading, setLoading ] = useState(false)
  // Stripe ties a linked bank to the customer its session was opened for,
  // which the server resolves from the saved draft's donor, and refuses to
  // charge it through any other. Editing the email can move the draft to a
  // different donor, so a link only counts under the email it was made
  // with. Kept rather than cleared, so changing the email back restores it.
  const [ link, setLink ] = useState(NO_LINK)
  const [ selectedId, setSelectedId ] = useState(null)
  const [ error, setError ] = useState(null)
  const linked = link.email === donation.state.email
  const accounts = linked ? link.accounts : []
  const accountId = linked ? selectedId : null
  const bodyHeight = useRef()
  const { createIntent } = useStripeServices({ ...bankProps, i18n })

  // Opt-in per PWC (data.fullscreen_3ds): full-screen the widget while
  // Stripe's Financial Connections picker (a viewport-covering overlay
  // from a Stripe origin) is up, so it isn't cramped in a short embed.
  const fullscreen = bankProps.fullscreen_3ds === true
  const takeover = useModalTakeover({ enabled: fullscreen })

  // Fallback for when the takeover is off: grow the iframe so the FC
  // modal isn't cramped. Skipped whenever fullscreen_3ds is on — the
  // takeover full-screens the iframe instead (and where it can't act —
  // a standalone, non-embedded widget — the modal already has room, so
  // no mitigation is needed). So the two never both run.
  const handlePickerOpen = () => {
    if (fullscreen) return
    const body = document.body
    body.style.minHeight = bodyHeight.current = body.offsetHeight + 'px'
    body.style.transition = 'min-height 0.3s'
    setTimeout(() => body.style.minHeight = '740px', 10)
  }

  const handlePickerClose = () => {
    if (fullscreen) return
    const body = document.body
    body.style.minHeight = bodyHeight.current
    setTimeout(() => {
      body.style.transition = ''
      body.style.minHeight = ''
    }, 360)
  }

  const openPicker = async () => {
    // The email this link is made under (see `link`), read up front so the
    // draft below is saved with the same one.
    const { email } = donation.state
    try {
      setLoading(true)
      // Open the session for the saved draft, so the server links the bank
      // to the same Stripe customer that `process` later charges.
      const { errors, token } = await record.saveDraft({ order: ACH_DRAFT })
      if (errors) return failed(errors)

      const response = await fetch(bankProps.bankSessionsPath, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ order_id: token, enable_upsell }),
      })
      if (response.ok) {
        const { client_secret: clientSecret } = await response.json()
        handlePickerOpen()
        // We own the picker's open/close window here, so bracket it
        // explicitly (no-op when the takeover is disabled or unembedded).
        takeover.startListening()
        const { error, financialConnectionsSession } =
          await stripe.collectFinancialConnectionsAccounts({ clientSecret })
        if (error)
          setError(error.message)
        else {
          const { accounts } = financialConnectionsSession
          // None comes back when the donor closes the picker; keep the
          // previous link and selection.
          if (accounts.length) {
            setLink({ email, accounts })
            setSelectedId(accounts.find(isEligible)?.id ?? null)
          }
        }
      } else {
        setError(BANK_CONNECTION_ERROR)
      }
    } catch (error) {
      console.error(error)
      setError(BANK_CONNECTION_ERROR)
    } finally {
      takeover.release()
      handlePickerClose()
      setLoading(false)
    }
  }

  const updateConnectedDonation = async (token) => {
    const url = [ bankProps.stripeIntentsPath, token ].join('/')
    const options = {
      method: 'PATCH',
      headers: csrfHeaders(),
      body: JSON.stringify({ locale: i18n.locale, enable_upsell }),
    }
    return fetch(url, options)
  }

  const process = async () => {
    record.setProcessing(true)

    const subscription = subscriptionIntervals[donation.state.subscription]

    const { errors: draftErrors, token, serializedDonation } =
      await record.saveDraft({ order: ACH_DRAFT })

    if (draftErrors) return failed(draftErrors)

    const result = await createIntent(token, {
      financial_connections_account_id: accountId,
      subscription,
      enable_upsell,
    })

    if (result.errors) {
      // The server refused this bank link outright (linked under a different
      // Stripe customer); drop it so the button asks to connect a bank again
      // instead of resending the same one.
      if (result.code === 'ach_account_holder_mismatch') setLink(NO_LINK)
      return failed(result.errors)
    }

    // Upsell flow: the donation PI on the connected account needs
    // on-session frontend confirmation.
    if (result.client_secret && result.stripe_account) {
      try {
        const connectedStripe = await loadStripe(stripeKey, { stripeAccount: result.stripe_account, locale: i18n.locale })
        const { error } = await connectedStripe.confirmUsBankAccountPayment(result.client_secret)

        if (error) return failed({ base: [ error.message ] })

        // Second PATCH: record_tag now points to the confirmed payment_intent,
        // so the controller hits the card_payment_intent? path for fulfillment.
        // Fulfillment may also happen via webhook; show thank-you regardless.
        updateConnectedDonation(token)
      } catch (error) {
        console.error(error)
        return failed({ base: [ BANK_CONNECTION_ERROR ] })
      }
    }

    return finished({ token, serializedDonation })
  }

  const eligibleAccounts = accounts.filter(isEligible)

  return {
    openPicker, process, accounts, eligibleAccounts, accountId,
    setAccountId: setSelectedId, error, loading,
  }
}

export default useBank
